import { Button, Checkbox, Popover } from "antd";
import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";

import { categoryApi, toArray } from "../../Services";
import { usePanelOrgTree } from "../../Hooks/usePanelOrgTree";
import { usePanelSettings } from "../../Hooks/usePanelSettings";
import { procurementApproaches as APPROACHES } from "./Constants";
import styles from "./WorkOrder.module.scss";

/** Multi-select values per section; an empty array means "not filtered". */
export interface WorkOrderFilters {
  procurementApproach: string[];
  category: string[];
  subsidiary: string[];
  costCentre: string[];
}

export const emptyWorkOrderFilters: WorkOrderFilters = {
  procurementApproach: [],
  category: [],
  subsidiary: [],
  costCentre: [],
};

/**
 * The filters as the list endpoints' query params — comma-separated lists, the
 * approach as its code (EMERGENCY, …), cost centre as `division`. Both lists
 * build their request from this; Procurement Approach was never sent, so the
 * list came back unfiltered.
 */
export const filterParams = (filters: WorkOrderFilters) => {
  const list = (values: string[]) => values.join(",") || undefined;
  return {
    procurementApproach: list(
      filters.procurementApproach.map((label) => APPROACHES.find((entry) => entry.label === label)?.value ?? label)
    ),
    category: list(filters.category),
    subsidiary: list(filters.subsidiary),
    division: list(filters.costCentre),
  };
};

interface Props {
  value: WorkOrderFilters;
  onApply: (filters: WorkOrderFilters) => void;
}

type SectionKey = keyof WorkOrderFilters;

/**
 * Filter popover behind the funnel button on the Work Order list.
 *
 * Each section header is a tri-state checkbox that selects/clears every option
 * beneath it (indeterminate when only some are picked) plus a chevron that
 * expands the option list. Options are themselves checkboxes, so a section can
 * carry several values at once.
 *
 * Cost Centre narrows to the cost centres of whichever subsidiaries are
 * ticked, falling back to every configured one when no subsidiary is chosen —
 * the same relationship the Add Work Order wizard enforces with its cascade.
 *
 * Both lists come from what Panel Contract has configured (see
 * `usePanelOrgTree`), not from the ePOD org tree, so an unconfigured
 * subsidiary or cost centre is never offered here.
 */
export const WorkOrderFilter = ({ value, onApply }: Props) => {
  const { subsidiaries, costCentresFor } = usePanelOrgTree();
  /* Normal is always filterable; Emergency is the entry Manage Catalog ->
     Settings governs, so it appears only while that toggle is on. */
  const { emergencyEnabled } = usePanelSettings();
  const procurementApproaches =
    emergencyEnabled === true
      ? ["Normal (Rotation Basis)", "Emergency (Rotation/Selective)"]
      : ["Normal (Rotation Basis)"];
  /* Categories come from Manage Catalog rather than a hardcoded list, so one
     added there shows up here without a code change. De-duplicated by name:
     the catalog allows two entries with the same name under different ids. */
  const [categories, setCategories] = useState<string[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    categoryApi
      .getAll()
      .then((rows: any) =>
        setCategories(
          Array.from(
            new Set(
              toArray(rows)
                .map((row: any) => String(row.categoryName ?? "").trim())
                .filter(Boolean)
            )
          ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
        )
      )
      .catch(() => setCategories([]));
  }, []);
  const [draft, setDraft] = useState<WorkOrderFilters>(value);
  const [expanded, setExpanded] = useState<SectionKey | null>(null);

  const optionsFor = (key: SectionKey): string[] => {
    switch (key) {
      case "procurementApproach":
        return procurementApproaches;
      case "category":
        return categories;
      case "subsidiary":
        return subsidiaries;
      case "costCentre":
        return costCentresFor(draft.subsidiary);
      default:
        return [];
    }
  };

  const sections: { key: SectionKey; label: string }[] = [
    { key: "procurementApproach", label: "Procurement Approach" },
    { key: "category", label: "Category" },
    { key: "subsidiary", label: "Select Subsidiary/Division" },
    { key: "costCentre", label: "Select Cost Centre" },
  ];

  /** Header checkbox: ticking selects every option, unticking clears them. */
  const toggleAll = (key: SectionKey, checked: boolean) => {
    const next: WorkOrderFilters = { ...draft, [key]: checked ? optionsFor(key) : [] };

    // Narrowing the subsidiaries can orphan already-picked cost centres.
    if (key === "subsidiary") {
      const permitted = costCentresFor(next.subsidiary);
      next.costCentre = next.costCentre.filter((costCentre) => permitted.includes(costCentre));
    }

    setDraft(next);
    if (checked) setExpanded(key);
  };

  const setSelection = (key: SectionKey, selected: string[]) => {
    const next: WorkOrderFilters = { ...draft, [key]: selected };

    if (key === "subsidiary") {
      const permitted = new Set(costCentresFor(selected));
      next.costCentre = draft.costCentre.filter((costCentre) => permitted.has(costCentre));
    }

    setDraft(next);
  };

  const content = (
    <div className={styles.filterPanel}>
      {sections.map((section) => {
        const options = optionsFor(section.key);
        const selected = draft[section.key];
        const allSelected = options.length > 0 && selected.length === options.length;
        const isExpanded = expanded === section.key;

        return (
          <div key={section.key}>
            <div className={`${styles.filterRow} ${selected.length ? styles.filterRowActive : ""}`}>
              <Checkbox
                checked={allSelected}
                indeterminate={selected.length > 0 && !allSelected}
                onChange={(e) => toggleAll(section.key, e.target.checked)}
              >
                {section.label}
              </Checkbox>
              <Icon
                icon={isExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
                className={styles.filterChevron}
                onClick={() => setExpanded(isExpanded ? null : section.key)}
              />
            </div>

            {isExpanded && (
              <div className={styles.filterOptions}>
                <Checkbox.Group
                  value={selected}
                  onChange={(checked) => setSelection(section.key, checked as string[])}
                >
                  {options.map((option: string, index: number) => (
                    <div
                      className={styles.filterOption}
                      key={`${index}`}
                    >
                      <Checkbox value={option}>{option}</Checkbox>
                    </div>
                  ))}
                </Checkbox.Group>
                {!options.length && (
                  <span className={styles.filterEmpty}>No options available</span>
                )}
              </div>
            )}

            <div className={styles.filterDivider} />
          </div>
        );
      })}

      <div className={styles.filterActions}>
        {/* Clears every section and shows the default list straight away.
            There was only Apply, so unticking and closing brought the old
            ticks back on reopen and nothing ever reset the list. */}
        <Button
          onClick={() => {
            setDraft(emptyWorkOrderFilters);
            onApply(emptyWorkOrderFilters);
            setOpen(false);
          }}
        >
          Reset
        </Button>
        <Button
          type="primary"
          onClick={() => {
            onApply(draft);
            setOpen(false);
          }}
        >
          Apply
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      overlayClassName={styles.filterPopover}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={(next) => {
        // Re-sync the draft from the applied filters each time it reopens, so
        // an abandoned edit does not linger.
        if (next) setDraft(value);
        setOpen(next);
      }}
    >
      <Button
        type="primary"
        className={styles.filterButton}
        icon={
          <Icon
            icon="mdi:filter-variant"
            inline
          />
        }
      />
    </Popover>
  );
};
